import axios from 'axios';
import { ReactNode, useMemo } from 'react';
import IotsApiContext, { IotsApiContextValue } from '../../contexts/iotsApi';

import { useAuthManager } from '../../contexts/authManager';
import {
  addContractInsurance,
  addCrewMemberCertificate,
  addVesselCertificate,
  addVesselInsurance,
  approveCrewMemberCertificate,
  approveInvoice,
  approveJourney,
  approveVesselCertificate,
  cancelJourney,
  createContract,
  createContractRoute,
  createCrewMember,
  createInvoice,
  createIsland,
  createJourney,
  createOperator,
  createPort,
  createUser,
  createVessel,
  deleteAdhocJourney,
  deleteContract,
  deleteContractInsurance,
  deleteContractRoute,
  deleteCrewMember,
  deleteCrewMemberCertificate,
  deletePort,
  deleteVessel,
  deleteVesselCertificate,
  deleteVesselInsurance,
  fetchAdminDashboardInfo,
  fetchAdminDashboardPendingInvoicesInfo,
  fetchAppData,
  fetchContractAssignedVessels,
  fetchContractById,
  fetchContractInsurancesById,
  fetchContractRouteDeletionImpactedJourneys,
  fetchContractRoutes,
  fetchContracts,
  fetchCrewCertificatesById,
  fetchCrewMemberById,
  fetchCrewMembers,
  fetchFile,
  fetchInvoiceById,
  fetchInvoiceJourneysByContractId,
  fetchInvoiceJourneysByInvoiceId,
  fetchInvoiceRejectionNotes,
  fetchInvoices,
  fetchIslandById,
  fetchIslands,
  fetchJourneyById,
  fetchJourneyLog,
  fetchJourneyNotes,
  fetchJourneyPlaceholders,
  fetchOperatorById,
  fetchOperatorDashboardInfo,
  fetchOperators,
  fetchPorts,
  fetchPublicIslands,
  fetchPublicJourneysByIslandId,
  fetchUserById,
  fetchUserOperator,
  fetchUsers,
  fetchVesselById,
  fetchVesselCertificatesById,
  fetchVesselContractAssignments,
  fetchVesselInsurancesById,
  fetchVessels,
  fetchYearAnalytics,
  fetchYearAnalyticsCsvFile,
  rejecInvoice,
  rejectCrewMemberCertificate,
  rejectJourney,
  rejectVesselCertificate,
  requestAnalyticsRecalculation,
  rescheduleJourney,
  rescindContract,
  rescindContractRoute,
  resendEmailInvitation,
  submitJourney,
  unsubmitJourney,
  updateContract,
  updateContractAssignedVessels,
  updateCrewMember,
  updateIsland,
  updateIslandVisibility,
  updateJourneyLog,
  updateOperator,
  updateOperatorState,
  updatePort,
  updateUser,
  updateUserState,
  updateVessel,
  updateVesselCertificate,
  validateInvoice,
} from '../../utils/fetchData';

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

const IotsApiProvider = ({ children }: { children?: ReactNode }) => {
  const { getAuthBearerToken } = useAuthManager();

  const value = useMemo<IotsApiContextValue>(() => {
    const client = axios.create({
      baseURL: BASE_URL,
    });

    client.interceptors.request.use(async config => {
      try {
        const bearerToken = await getAuthBearerToken();
        if (bearerToken) {
          config.headers.set('Authorization', `Bearer ${bearerToken}`);
        }
        return config;
      } catch (error) {
        console.error('Failed to check bearer token', error);
      }

      return config;
    });

    return {
      createUser: user => createUser(user, client),
      getUserById: userId => fetchUserById(client, userId),
      getUsers: () => fetchUsers(client),
      updateUser: user => updateUser(user, client),
      updateUserState: (id, newIsActive) =>
        updateUserState(id, newIsActive, client),
      resendEmailInvitation: user => resendEmailInvitation(user, client),
      createOperator: operator => createOperator(operator, client),
      getOperatorById: operatorId => fetchOperatorById(operatorId, client),
      updateOperator: operator => updateOperator(operator, client),
      getOperators: () => fetchOperators(client),
      updateOperatorState: operator => updateOperatorState(operator, client),
      createIsland: island => createIsland(island, client),
      updateIsland: (islandId, island) =>
        updateIsland(islandId, island, client),
      updateIslandVisibility: islandId =>
        updateIslandVisibility(islandId, client),
      getIslands: () => fetchIslands(client),
      getIslandById: id => fetchIslandById(id, client),
      createPort: port => createPort(port, client),
      updatePort: (portId, port) => updatePort(portId, port, client),
      deletePort: portId => deletePort(portId, client),
      getPorts: () => fetchPorts(client),
      createContract: contract => createContract(contract, client),
      deleteContract: contractId => deleteContract(contractId, client),
      rescindContract: contractId => rescindContract(contractId, client),
      getContracts: () => fetchContracts(client),
      getContractById: contractId => fetchContractById(contractId, client),
      getContractRoutes: contractId => fetchContractRoutes(contractId, client),
      createContractRoute: (contractId, newRoute) =>
        createContractRoute(contractId, newRoute, client),
      getContractRouteDeletionImpactedJourneys: (contractId, routeId) =>
        fetchContractRouteDeletionImpactedJourneys(contractId, routeId, client),
      deleteContractRoute: (contractId, routeId) =>
        deleteContractRoute(contractId, routeId, client),
      rescindContractRoute: (contractId, routeId) =>
        rescindContractRoute(contractId, routeId, client),
      getContractAssignedVessels: contractId =>
        fetchContractAssignedVessels(contractId, client),
      updateContractAssignedVessels: (contractId, newAssignedVessels) =>
        updateContractAssignedVessels(contractId, newAssignedVessels, client),
      updateContract: contract => updateContract(contract, client),
      getVessels: () => fetchVessels(client),
      getVesselById: vesselId => fetchVesselById(vesselId, client),
      createVessel: vessel => createVessel(vessel, client),
      updateVessel: vessel => updateVessel(vessel, client),
      deleteVessel: vesselId => deleteVessel(vesselId, client),
      getVesselCertificates: vesselId =>
        fetchVesselCertificatesById(vesselId, client),
      addVesselCertificate: (vesselId, certificate) =>
        addVesselCertificate(vesselId, certificate, client),
      updateVesselCertificate: (vesselId, certificate) =>
        updateVesselCertificate(vesselId, certificate, client),
      approveVesselCertificate: (vesselId, certificateId) =>
        approveVesselCertificate(vesselId, certificateId, client),
      rejectVesselCertificate: (vesselId, certificateId, note) =>
        rejectVesselCertificate(vesselId, certificateId, note, client),
      deleteVesselCertificate: (vesselId, certificateId) =>
        deleteVesselCertificate(vesselId, certificateId, client),
      getVesselContractAssignments: vesselId =>
        fetchVesselContractAssignments(vesselId, client),
      getUserOperator: () => fetchUserOperator(client),
      getAppData: () => fetchAppData(client),
      getFile: url => fetchFile(client, url),
      getCrewMembers: () => fetchCrewMembers(client),
      createCrewMember: crewMember => createCrewMember(crewMember, client),
      updateCrewMember: crewMember => updateCrewMember(crewMember, client),
      deleteCrewMember: crewMemberId => deleteCrewMember(crewMemberId, client),
      getCrewMemberById: crewMemberId =>
        fetchCrewMemberById(crewMemberId, client),
      getCrewMemberCertificates: crewMemberId =>
        fetchCrewCertificatesById(crewMemberId, client),
      addCrewMemberCertificate: (crewMemberId, certificate) =>
        addCrewMemberCertificate(crewMemberId, certificate, client),
      approveCrewMemberCertificate: (crewMemberId, certificateId) =>
        approveCrewMemberCertificate(crewMemberId, certificateId, client),
      rejectCrewMemberCertificate: (crewMemberId, certificateId, note) =>
        rejectCrewMemberCertificate(crewMemberId, certificateId, note, client),
      deleteCrewMemberCertificate: (crewMemberId, certificateId) =>
        deleteCrewMemberCertificate(crewMemberId, certificateId, client),
      createJourney: journey => createJourney(journey, client),
      getJourneyById: journeyId => fetchJourneyById(journeyId, client),
      getJourneyLog: journeyId => fetchJourneyLog(journeyId, client),
      updateJourneyLog: updatedJourneyLog =>
        updateJourneyLog(updatedJourneyLog, client),
      rescheduleJourney: journeyChangePayload =>
        rescheduleJourney(journeyChangePayload, client),
      cancelJourney: journeyChangePayload =>
        cancelJourney(journeyChangePayload, client),
      deleteAdhocJourney: journeyId => deleteAdhocJourney(journeyId, client),
      submitJourney: journeyId => submitJourney(journeyId, client),
      unsubmitJourney: journeyId => unsubmitJourney(journeyId, client),
      approveJourney: journeyId => approveJourney(journeyId, client),
      rejectJourney: (journeyId, note) =>
        rejectJourney(journeyId, note, client),
      getJourneyPlaceholders: (startDate, endDate, contractId) =>
        fetchJourneyPlaceholders(startDate, endDate, contractId, client),
      getJourneyNotes: journeyId => fetchJourneyNotes(journeyId, client),
      getInvoices: () => fetchInvoices(client),
      getInvoiceJourneysByContractId: (contractId, year, month) =>
        fetchInvoiceJourneysByContractId(contractId, year, month, client),
      validateInvoice: (contractId: string, year, month) =>
        validateInvoice(contractId, year, month, client),
      createInvoice: invoice => createInvoice(invoice, client),
      getInvoiceById: invoiceId => fetchInvoiceById(invoiceId, client),
      getInvoiceJourneysByInvoiceId: invoiceId =>
        fetchInvoiceJourneysByInvoiceId(invoiceId, client),
      approveInvoice: invoiceId => approveInvoice(invoiceId, client),
      rejectInvoice: (invoiceId, note) => rejecInvoice(invoiceId, note, client),
      getInvoiceRejectionNotes: invoiceId =>
        fetchInvoiceRejectionNotes(invoiceId, client),
      getPublicIslands: () => fetchPublicIslands(client),
      getPublicJourneysByIslandId: islandId =>
        fetchPublicJourneysByIslandId(islandId, client),
      getOperatorDashboardInfo: () => fetchOperatorDashboardInfo(client),
      getAdminDashboardInfo: () => fetchAdminDashboardInfo(client),
      getAdminDashboardPendingInvoiceInfo: () =>
        fetchAdminDashboardPendingInvoicesInfo(client),
      getYearAnalytics: year => fetchYearAnalytics(year, client),
      getYearAnalyticsCsvFile: year => fetchYearAnalyticsCsvFile(year, client),
      requestAnalyticsRecalculation: () =>
        requestAnalyticsRecalculation(client),
      getVesselInsurances: vesselId =>
        fetchVesselInsurancesById(vesselId, client),
      addVesselInsurance: (vesselId, insurance) =>
        addVesselInsurance(vesselId, insurance, client),
      deleteVesselInsurance: (vesselId, insuranceId) =>
        deleteVesselInsurance(vesselId, insuranceId, client),
      getContractInsurances: contractId =>
        fetchContractInsurancesById(contractId, client),
      addContractInsurance: (contractId, insurance) =>
        addContractInsurance(contractId, insurance, client),
      deleteContractInsurance: (contractId, insuranceId) =>
        deleteContractInsurance(contractId, insuranceId, client),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <IotsApiContext.Provider value={value}>{children}</IotsApiContext.Provider>
  );
};

export default IotsApiProvider;
